import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import environment from "../../environments/environment";
import { RejectMigrationRequest } from "@app/interface/models";

@Injectable({ providedIn: "root" })
export class ApiService {
  apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /** GET companies  */
  getCompanies(): Observable<any> {
    return this.http.get<any>(this.apiUrl + "stage/iris_companies").pipe(
      map((res: any) => res),
      catchError(this.handleError),
    );
  }
  /** GET company protocols  */
  getCompanyProtocols(company_id: number): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + "stage/protocols-to-migrate/" + company_id)
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }
  /** GET migration exists for company  */
  getMigrationByCompany(company_id: number): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + "migration/migration-status/" + company_id)
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }
  /** Push company migration  */
  public pushCompany(company_id: number): Observable<any> {
    return this.http
      .post<any>(this.apiUrl + "stage/push-company/" + company_id, null)
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }
  /** Reject company migration  */
  public rejectCompanyMigration(
    company_id: number,
    data: RejectMigrationRequest,
  ): Observable<any> {
    return this.http
      .post<RejectMigrationRequest>(
        this.apiUrl + "stage/reject/" + company_id,
        data,
      )
      .pipe(
        map((res: RejectMigrationRequest) => res),
        catchError(this.handleError),
      );
  }

  /** GET migrations  */
  getMigrations(): Observable<any> {
    return this.http.get<any>(this.apiUrl + "migration/").pipe(
      map((res: any) => res),
      catchError(this.handleError),
    );
  }

  /** GET deleted/rejected migrations */
  getDeletedMigrations(): Observable<any> {
    return this.http.get<any>(this.apiUrl + "migration/deleted").pipe(
      map((res: any) => res),
      catchError(this.handleError),
    );
  }

  /** GET migration by id  */
  getMigrationDetails(id: number): Observable<any> {
    return this.http.get<any>(this.apiUrl + "migration/" + id + "/").pipe(
      map((res: any) => res),
      catchError(this.handleError),
    );
  }

  getDeletedMigrationDetails(id: number): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + "migration/deleted/" + id + "/")
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  /** GET migration by id  */
  getMigrationMetrics(id: number): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + "migration/" + id + "/metric/")
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  getFalloutRecords(
    companyId?: number,
    migrationId?: number,
  ): Observable<unknown[] | unknown> {
    if (!migrationId) {
      return new Observable((observer) => {
        observer.next([]);
        observer.complete();
      });
    }

    const url = this.apiUrl + `stage/migration/${migrationId}/fallout-details`;

    return this.http.get<unknown[]>(url).pipe(
      map((res: unknown) => {
        if (!companyId || !Array.isArray(res)) {
          return res;
        }

        return res.filter(
          (record: unknown): record is { company_id: number } => {
            if (
              !record ||
              typeof record !== "object" ||
              !("company_id" in record)
            ) {
              return false;
            }

            return record.company_id === companyId;
          },
        );
      }),
      catchError(this.handleError),
    );
  }

  getPlaceHolders(): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + "migration/placeholder-values")
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   *
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError = (error: HttpErrorResponse): any => {
    const newError: any = new Error(`${error}`);
    newError.details = error;

    return throwError(() => newError);
  };
}
